import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { searchBlocksInstant } from "@/api/query-api";
import { BlockBody, blockCaption, blockMediaUrl, blockTimestamp, formatDate, relativeTime, } from "./block-visuals";
import "./search-modal.scss";
const NON_ALPHA_SECTION = "#";
function sectionKeyFor(title) {
    const first = title.trim().charAt(0).toUpperCase();
    return first >= "A" && first <= "Z" ? first : NON_ALPHA_SECTION;
}
function groupAlphabetical(collections) {
    const buckets = new Map();
    for (const c of collections) {
        const key = sectionKeyFor(c.title);
        const bucket = buckets.get(key);
        if (bucket)
            bucket.push(c);
        else
            buckets.set(key, [c]);
    }
    const keys = Array.from(buckets.keys()).sort((a, b) => {
        if (a === NON_ALPHA_SECTION)
            return -1;
        if (b === NON_ALPHA_SECTION)
            return 1;
        return a.localeCompare(b);
    });
    return keys.map((letter) => ({
        letter,
        items: (buckets.get(letter) ?? []).slice().sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" })),
    }));
}
const MagnifierIcon = () => (_jsxs("svg", { className: "vld-search__glass", width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", "aria-hidden": "true", children: [_jsx("circle", { cx: "7", cy: "7", r: "5", stroke: "currentColor", strokeWidth: "1.6" }), _jsx("line", { x1: "10.8", y1: "10.8", x2: "14", y2: "14", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round" })] }));
const CollectionRow = ({ collection, onSelect }) => (_jsxs("button", { type: "button", className: "vld-search-row", style: { ["--row-color"]: collection.color }, onClick: () => onSelect(collection.id), children: [_jsx("span", { className: "vld-search-row__title", children: collection.title }), collection.count !== undefined ? (_jsx("span", { className: "vld-search-row__count", children: collection.count })) : undefined] }));
const CollectionsScope = ({ collections, query, sort, projectId, onSelect }) => {
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return collections.filter((c) => {
            if (projectId && c.projectId !== projectId)
                return false;
            if (q && !c.title.toLowerCase().includes(q))
                return false;
            return true;
        });
    }, [collections, query, projectId]);
    if (filtered.length === 0) {
        return _jsx("div", { className: "vld-search-empty", children: "No collections match." });
    }
    if (sort === "recent") {
        const ordered = filtered.slice().sort((a, b) => b.recencyMs - a.recencyMs);
        return (_jsx("div", { className: "vld-search-index", children: _jsx("div", { className: "vld-search-index__section", children: ordered.map((c) => (_jsx(CollectionRow, { collection: c, onSelect: onSelect }, c.id))) }) }));
    }
    const sections = groupAlphabetical(filtered);
    return (_jsx("div", { className: "vld-search-index", children: sections.map((section) => (_jsxs("section", { className: "vld-search-index__section", children: [_jsx("h3", { className: "vld-search-index__letter", children: section.letter }), section.items.map((c) => (_jsx(CollectionRow, { collection: c, onSelect: onSelect }, c.id)))] }, section.letter))) }));
};
const SearchResultTile = ({ block, onSelect }) => {
    const media = blockMediaUrl(block);
    const ts = blockTimestamp(block);
    return (_jsxs("div", { className: "vlab-tile", "data-vld-block-id": block.id, children: [_jsx("div", { className: "vld-tile-frame", children: _jsx("button", { type: "button", className: "vlab-tile__frame vld-hit vld-search-tile__hit", onClick: () => onSelect(block), children: media ? (_jsx("img", { className: "vld-search-tile__media", src: media, alt: block.title ?? "", loading: "lazy" })) : (_jsx(BlockBody, { block: block })) }) }), _jsxs("div", { className: "vlab-tile__caption", children: [_jsx("span", { className: "vlab-tile__caption-name", children: blockCaption(block) }), _jsx("span", { className: "vlab-tile__caption-by", title: formatDate(ts), children: relativeTime(ts) })] })] }));
};
const BlocksScope = ({ query, onSelect }) => {
    const [debounced, setDebounced] = useState(query);
    useEffect(() => {
        const id = window.setTimeout(() => setDebounced(query), 250);
        return () => window.clearTimeout(id);
    }, [query]);
    const trimmed = debounced.trim();
    const enabled = trimmed.length >= 2;
    const { data, isLoading, isError } = useQuery({
        queryKey: ["alpha-search", "blocks-instant", debounced],
        enabled,
        queryFn: () => searchBlocksInstant({ query: trimmed, limit: 30 }),
    });
    if (!enabled) {
        return _jsx("div", { className: "vld-search-empty", children: "Type to search blocks." });
    }
    if (isLoading) {
        return _jsx("div", { className: "vld-search-empty", children: "Searching\u2026" });
    }
    if (isError) {
        return _jsx("div", { className: "vld-search-empty", children: "Search failed." });
    }
    const results = data?.results ?? [];
    if (results.length === 0) {
        return _jsx("div", { className: "vld-search-empty", children: "No blocks found." });
    }
    return (_jsx("div", { className: "vld-search-grid", children: results.map((item) => (_jsx(SearchResultTile, { block: item.block, onSelect: onSelect }, item.block.id))) }));
};
export const SearchModal = ({ open, onClose, collections, projects, onOpenCollection, onOpenBlock, initialScope = "collections", }) => {
    const [scope, setScope] = useState(initialScope);
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState("alphabetical");
    // "" → all projects; otherwise a project (group) id to scope the index to.
    const [projectId, setProjectId] = useState("");
    const inputRef = useRef(null);
    // Reset to the requested scope (and clear the project filter) on open.
    useEffect(() => {
        if (open) {
            setScope(initialScope);
            setProjectId("");
        }
    }, [open, initialScope]);
    // Autofocus + select the search field on open.
    useEffect(() => {
        if (!open)
            return;
        const id = window.setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        }, 0);
        return () => window.clearTimeout(id);
    }, [open]);
    // Refocus when the scope changes while open.
    useEffect(() => {
        if (open)
            inputRef.current?.focus();
    }, [scope, open]);
    // Esc closes.
    useEffect(() => {
        if (!open)
            return;
        const onKeyDown = (e) => {
            if (e.key === "Escape")
                onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, onClose]);
    if (!open)
        return undefined;
    const selectCollection = (id) => {
        onClose();
        onOpenCollection(id);
    };
    const selectBlock = (block) => {
        onClose();
        onOpenBlock(block.collectionId, block.id);
    };
    const placeholder = scope === "collections" ? "Filter index" : "Search blocks";
    return (_jsx("div", { className: "vld-search-overlay", onMouseDown: (e) => {
            if (e.target === e.currentTarget)
                onClose();
        }, children: _jsxs("div", { className: "vld-search__panel", role: "dialog", "aria-modal": "true", "aria-label": "Search", children: [_jsxs("div", { className: "vld-search__head", children: [_jsxs("div", { className: "vld-search__scope", role: "tablist", "aria-label": "Search scope", children: [_jsx("button", { type: "button", role: "tab", "aria-selected": scope === "collections", className: `vld-search__scope-btn${scope === "collections" ? " is-active" : ""}`, onClick: () => setScope("collections"), children: "Collections" }), _jsx("button", { type: "button", role: "tab", "aria-selected": scope === "blocks", className: `vld-search__scope-btn${scope === "blocks" ? " is-active" : ""}`, onClick: () => setScope("blocks"), children: "Blocks" })] }), _jsxs("div", { className: "vld-search__field", children: [_jsx(MagnifierIcon, {}), _jsx("input", { ref: inputRef, className: "vld-search__input", type: "text", value: query, placeholder: placeholder, "aria-label": placeholder, onChange: (e) => setQuery(e.target.value) })] }), scope === "collections" && projects.length > 1 ? (_jsxs("select", { className: "vld-search__project", "aria-label": "Look through project", value: projectId, onChange: (e) => setProjectId(e.target.value), children: [_jsx("option", { value: "", children: "All projects" }), projects.map((p) => (_jsx("option", { value: p.id, children: p.title }, p.id)))] })) : undefined, scope === "collections" ? (_jsxs("div", { className: "vld-search__sort", role: "group", "aria-label": "Sort order", children: [_jsx("button", { type: "button", className: `vld-search__sort-btn${sort === "recent" ? " is-active" : ""}`, "aria-pressed": sort === "recent", onClick: () => setSort("recent"), children: "Recent updates" }), _jsx("button", { type: "button", className: `vld-search__sort-btn${sort === "alphabetical" ? " is-active" : ""}`, "aria-pressed": sort === "alphabetical", onClick: () => setSort("alphabetical"), children: "Alphabetical" })] })) : undefined, _jsx("button", { type: "button", className: "vld-search__close", "aria-label": "Close search", onClick: onClose, children: "\u00D7" })] }), _jsx("div", { className: "vld-search__body", children: scope === "collections" ? (_jsx(CollectionsScope, { collections: collections, query: query, sort: sort, projectId: projectId, onSelect: selectCollection })) : (_jsx(BlocksScope, { query: query, onSelect: selectBlock })) })] }) }));
};
